import { db } from '@/features/offline/db'
import type { CatalogCard } from '@/types/card'

/**
 * Was ist neu, was hat sich geändert?
 *
 * Diese Datei beantwortet die Frage — und zwar als einzige. Sie wird an drei Stellen
 * gebraucht: beim Öffnen der App, in der Kennzeichnung einzelner Karten und im Service
 * Worker, wenn eine Push-Nachricht eintrifft. Stünde die Regel an mehreren Stellen,
 * würde eine davon irgendwann abweichen und dem Nutzer etwas anderes sagen als die
 * andere.
 *
 * Bewusst ohne DOM-Zugriff: Der Service Worker hat kein `window`.
 */

const SEEN_KEY = 'updates:seen'

/**
 * Der Kartenbestand, wie der Nutzer ihn zuletzt zur Kenntnis genommen hat.
 *
 * Bewusst je Karte und nicht als ein Katalog-Hash: Wer eine der drei geänderten Karten
 * öffnet, hat genau diese gesehen — die beiden anderen stehen weiterhin aus. Ein
 * einzelner Hash über den ganzen Katalog könnte das nicht abbilden.
 */
export interface SeenState {
  /** Karten-ID → Kennung ihres damaligen Standes. */
  stands: Record<string, string>
  at: number
}

export interface CatalogUpdates {
  neu: CatalogCard[]
  aktualisiert: CatalogCard[]
}

export const KEINE_UPDATES: CatalogUpdates = { neu: [], aktualisiert: [] }

/**
 * Kennung eines Kartenstandes.
 *
 * Der Hash der PDF allein genügt nicht: Wird nur die Beschreibung oder ein Schlagwort
 * in card.yaml geändert, bleibt die PDF gleich — die Karte ist trotzdem eine andere.
 * Die Versionsangabe ist genau das Feld, das die Redaktion laut README bei jeder
 * inhaltlichen Änderung hochsetzt.
 */
function stand(card: CatalogCard): string {
  return `${card.pdfHash}:${card.version}`
}

/**
 * Vergleicht den Katalog mit dem zuletzt gesehenen Stand.
 *
 * Entfernte Karten tauchen bewusst nicht auf: Sie sind aus dem Katalog verschwunden,
 * es gibt nichts mehr zu öffnen und nichts zu melden.
 */
export function diffCatalog(seen: SeenState | null, karten: readonly CatalogCard[]): CatalogUpdates {
  // Erster Start: Der gesamte Bestand ist für diesen Nutzer neu — ihn deshalb mit
  // „9 neue Karten" zu begrüßen, wäre eine Meldung ohne Aussage.
  if (!seen) return KEINE_UPDATES

  const neu: CatalogCard[] = []
  const aktualisiert: CatalogCard[] = []

  for (const card of karten) {
    const bekannt = seen.stands[card.id]
    if (bekannt === undefined) neu.push(card)
    else if (bekannt !== stand(card)) aktualisiert.push(card)
  }

  return { neu, aktualisiert }
}

export function anzahlUpdates(updates: CatalogUpdates): number {
  return updates.neu.length + updates.aktualisiert.length
}

/**
 * Der Satz, mit dem die Änderungen benannt werden — in der App wie in der
 * Push-Nachricht derselbe, damit niemand zwei verschiedene Zahlen zu sehen bekommt.
 */
export function beschreibeUpdates(updates: CatalogUpdates): string {
  const teile: string[] = []
  if (updates.neu.length > 0) {
    teile.push(updates.neu.length === 1 ? 'eine neue Karte' : `${updates.neu.length} neue Karten`)
  }
  if (updates.aktualisiert.length > 0) {
    teile.push(
      updates.aktualisiert.length === 1
        ? 'eine aktualisierte Karte'
        : `${updates.aktualisiert.length} aktualisierte Karten`,
    )
  }
  if (teile.length === 0) return 'Der Kartenbestand ist unverändert.'
  return `Es gibt ${teile.join(' und ')}.`
}

export async function readSeen(): Promise<SeenState | null> {
  const entry = await db.settings.get(SEEN_KEY)
  return (entry?.value as SeenState | undefined) ?? null
}

/** Hält fest, dass der Nutzer diesen Stand zur Kenntnis genommen hat. */
export async function markSeen(karten: readonly CatalogCard[]): Promise<SeenState> {
  const state: SeenState = {
    stands: Object.fromEntries(karten.map((card) => [card.id, stand(card)])),
    at: Date.now(),
  }
  await db.settings.put({ key: SEEN_KEY, value: state })
  return state
}

/**
 * Hält fest, dass der Nutzer diese eine Karte in ihrem aktuellen Stand gesehen hat.
 *
 * Wird beim Öffnen einer Karte aufgerufen. Ohne das bliebe eine Karte als „neu"
 * gekennzeichnet, obwohl sie gerade gelesen wurde — und der Hinweis käme bei jedem
 * Start zurück, selbst wenn längst alles zur Kenntnis genommen wurde.
 */
export async function markCardSeen(card: CatalogCard): Promise<void> {
  const seen = await readSeen()
  // Kein Eintrag heißt: erster Start, es gilt ohnehin alles als bekannt.
  if (!seen) return
  if (seen.stands[card.id] === stand(card)) return

  seen.stands[card.id] = stand(card)
  await db.settings.put({ key: SEEN_KEY, value: seen })
}

/**
 * Ermittelt die Änderungen gegenüber dem letzten Besuch.
 *
 * Beim allerersten Aufruf wird der Stand still übernommen und nichts gemeldet.
 * Danach bleibt der gespeicherte Stand unangetastet — er ändert sich erst, wenn der
 * Nutzer den Hinweis bestätigt. Sonst verschwände die Meldung beim nächsten Öffnen
 * von selbst, obwohl niemand sie gelesen hat.
 */
export async function ermittleUpdates(karten: readonly CatalogCard[]): Promise<CatalogUpdates> {
  const seen = await readSeen()
  if (!seen) {
    await markSeen(karten)
    return KEINE_UPDATES
  }
  return diffCatalog(seen, karten)
}
